import { Box, Skeleton, useTheme } from '@mui/material';
import { AdviceBox } from '@portals/core/src/components/AdviceBox/AdviceBox';
import { Dialog } from '@portals/core/src/components/Dialog/Dialog';
import { Grid } from '@portals/core/src/components/Grid/Grid';
import type { Theme } from '@portals/core/src/themes/themes';
import { InfoCircleOutline } from '@portals/icons';
import { useRouter } from 'next/router';
import React, { ReactElement, useEffect } from 'react';

import type { Node } from '../../consumers/Consumer/Consumer';
import { useServiceBoxContext } from '../../contexts/service-box';

export interface ServiceBoxDialogProps {
  renderNode: (node: Node) => ReactElement;
}

function ServiceBoxSkeleton() {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={4}>
        <Skeleton variant="rounded" width={190} height={62} />
        <Skeleton variant="rounded" width={190} height={62} />
      </Box>
      <Grid container spacing={9} sx={{ pt: 8 }}>
        <Grid size={6}>
          <Skeleton variant="rectangular" width={268} height={220} />
        </Grid>
        <Grid size={6}>
          <Skeleton variant="rectangular" width={268} height={220} />
        </Grid>
      </Grid>
    </Box>
  );
}

function getMarginTop(theme: Theme) {
  const headers = document.getElementsByTagName('header');

  if (!headers.length) {
    return theme.spacing(8);
  }

  const header = headers[headers.length - 1];
  const rect = header.getBoundingClientRect();

  return `calc(${rect.top + rect.height}px + ${theme.spacing(8)})`;
}

function getMaxHeight(theme: Theme) {
  const headers = document.getElementsByTagName('header');

  if (!headers.length) {
    return `calc(100vh - ${theme.spacing(8)})`;
  }

  const header = headers[headers.length - 1];
  const rect = header.getBoundingClientRect();

  return `calc(100vh - ${rect.top + rect.height}px - ${theme.spacing(8)})`;
}

export const ServiceBoxDialog: React.FC<ServiceBoxDialogProps> = ({ renderNode }): JSX.Element => {
  const serviceBoxContext = useServiceBoxContext();
  const theme = useTheme<Theme>();
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = () => {
      serviceBoxContext?.onClose();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, serviceBoxContext]);

  if (!serviceBoxContext || !serviceBoxContext.open) {
    return null;
  }

  function renderContent() {
    if (serviceBoxContext.error) {
      return (
        <Box mt={10}>
          <AdviceBox variant="info" icon={<InfoCircleOutline />}>
            Es ist ein Fehler aufgetreten, bitte versuchen Sie es in Kürze erneut.
          </AdviceBox>
        </Box>
      );
    }

    if (serviceBoxContext.data) {
      return renderNode(serviceBoxContext.data);
    }

    return <ServiceBoxSkeleton />;
  }

  return (
    <Dialog
      disablePortal
      open={serviceBoxContext.open}
      PaperProps={{
        'aria-labelledby': undefined,
        'aria-label': serviceBoxContext.data?.ariaLabel,
        sx: {
          borderRadius: theme.shape.borderRadius,
          marginRight: `max(calc((100% - ${theme.breakpoints.values.xl}px) / 2 + ${theme.spacing(12)}), ${theme.spacing(
            12
          )})`,
          borderWidth: 1,
          borderColor: 'grey.300',
          marginTop: getMarginTop(theme),
          maxHeight: getMaxHeight(theme),
        },
      }}
      onClose={serviceBoxContext.onClose}
      fullWidth={true}
      maxWidth="sm"
      sx={{
        display: { xs: 'none', sm: 'none', md: 'block' },
        '& .MuiDialog-container': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        },
        '& .MuiPaper-root': {
          padding: 10,
        },
      }}
    >
      {renderContent()}
    </Dialog>
  );
};
